import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SparkleEffect } from '../components/SparkleEffect';
import { GlitterHeart } from '../components/GlitterHeart';
import { FlameIcon, UsersIcon } from 'lucide-react';
export function AboutPage() {
  return <div className="min-h-screen bg-white pt-24" data-id="element-106">
      {/* Hero Banner */}
      <section className="relative py-20 bg-blush-50 overflow-hidden" data-id="element-107">
        <SparkleEffect count={80} data-id="element-108" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-109">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="font-heading text-5xl md:text-6xl font-bold text-plum-700 mb-6" data-id="element-110">
            About Mandy
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-xl text-plum-500 max-w-2xl mx-auto font-medium" data-id="element-111">
            The messy, beautiful, unfiltered truth.
          </motion.p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20" data-id="element-112">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-113">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="prose prose-lg prose-plum mx-auto text-plum-700 leading-relaxed" data-id="element-114">
            <p className="text-2xl font-heading text-plum-700 mb-8 leading-snug font-bold" data-id="element-115">
              Let me be real with you — my life has not been a fairy tale.
            </p>

            <p className="mb-6" data-id="element-116">
              I've been around death since I was five years old. I'm a widow who
              lost her husband to cancer. I'm raising three incredible kids: an
              18-year-old, a 16-year-old battling bipolar disorder, and a
              7-year-old with autism. And if that wasn't enough, I've lived with
              ADHD and depression my entire life.
            </p>

            <p className="mb-6" data-id="element-117">
              Recently, I got laid off from my job. Instead of letting it break
              me, I said, <em data-id="element-118">"F*ck it. I'm going to help people."</em>
            </p>

            <p className="mb-6" data-id="element-119">
              That's what sparked me to become a mindset and life coach. I want
              to be the person I NEEDED when I was going through my hardest
              times. I want to help people get through whatever the hell is
              stopping them from succeeding in life.
            </p>

            <p className="mb-6" data-id="element-120">
              I am huge on body positivity, self-care, and actually taking care
              of yourself. These aren't just trendy buzzwords to me; they are
              survival tools. I'm big on boosting confidence, motivating, and
              inspiring people to see their own worth.
            </p>

            <p className="mb-6" data-id="element-121">
              Look, I have a sassy mouth and I cuss a lot. I don't do fake. But
              I've got a heart of gold, and all I want to do is help rebuild the
              world one soul at a time.
            </p>

            <p className="mb-10" data-id="element-122">
              Oh, and because I believe healing happens in many ways, I'm also
              becoming a travel agent. Why? Because everyone deserves a damn
              vacation. I want to build packages for people who generally can't
              afford to get away. Tell me your budget, even if it's small, and
              I'll find something that fits. Healing while traveling is real,
              and you deserve to experience it.
            </p>

            <div className="flex items-center gap-4 border-l-4 border-cta-500 pl-6 py-4 bg-blush-50 rounded-r-xl italic text-plum-700 font-medium mb-12" data-id="element-123">
              "I've been through hell and I'm still standing. Now, I'm here to
              help you do the same."
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-lavender-50" data-id="element-124">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-125">
          <div className="text-center mb-16" data-id="element-126">
            <h2 className="font-heading text-4xl font-bold text-plum-700 mb-4" data-id="element-127">
              How We Roll
            </h2>
            <p className="text-plum-500 text-lg" data-id="element-128">
              The core values that guide everything I do.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-129">
            {[{
            icon: <GlitterHeart size={32} data-id="element-130" />,
            title: 'No Fake Shit',
            desc: 'Showing up as our true, messy, beautiful selves. We leave the masks at the door.'
          }, {
            icon: <FlameIcon className="w-8 h-8 text-cta-500" data-id="element-131" />,
            title: 'Grow Through It',
            desc: "Embracing the uncomfortable moments because that's where the real badassery is built."
          }, {
            icon: <UsersIcon className="w-8 h-8 text-cta-500" data-id="element-132" />,
            title: 'We Rise Together',
            desc: "Healing is an inside job, but you don't have to do it alone. I've got your back."
          }].map((value, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.2
          }} className="bg-white p-8 rounded-[2rem] shadow-sm text-center border border-lavender-100" data-id="element-133">
                <div className="w-16 h-16 mx-auto bg-blush-50 rounded-full flex items-center justify-center mb-6" data-id="element-134">
                  {value.icon}
                </div>
                <h3 className="font-heading text-2xl font-bold text-plum-700 mb-3" data-id="element-135">
                  {value.title}
                </h3>
                <p className="text-plum-500 font-medium" data-id="element-136">{value.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 text-center" data-id="element-137">
        <h2 className="font-heading text-3xl md:text-4xl font-bold text-plum-700 mb-8" data-id="element-138">
          Ready to stop just surviving?
        </h2>
        <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-139">
          <Link to="/courses" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-cta-500 text-white font-bold hover:bg-cta-600 transition-colors shadow-md" data-id="element-140">
            <GlitterHeart size={18} variant="light" data-id="element-141" />
            Explore Courses
          </Link>
          <Link to="/coaching" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-blush-100 text-plum-700 font-bold hover:bg-blush-200 transition-colors" data-id="element-142">
            <GlitterHeart size={18} data-id="element-143" />
            Book 1:1 Coaching
          </Link>
        </div>
      </section>
    </div>;
}